import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, GraduationCap, DollarSign, FileText, Palette, Smartphone, Briefcase } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const MODULES = [
  { icon: FileText, title: "Copyright & Licensing", desc: "Understand ownership, publishing rights, and how to protect your work." },
  { icon: DollarSign, title: "Royalties & Revenue", desc: "SAMRO, SAMPRA, CCSA. Mechanical vs. performance royalties explained." },
  { icon: Briefcase, title: "Contracts & Negotiation", desc: "How to read, negotiate and sign record and distribution deals." },
  { icon: Palette, title: "Branding & Identity", desc: "Craft a visual and sonic identity that's distinctively you." },
  { icon: Smartphone, title: "Social Media Mastery", desc: "Grow an audience that believes in what you stand for." },
  { icon: GraduationCap, title: "Financial Literacy", desc: "Budgeting, taxes, and building wealth as a creative." },
];

const TESTIMONIALS = [
  { name: "Sibusiso K.", quote: "Before the programme I was signing contracts blindly. Now I lead my career with confidence." },
  { name: "Nomvula D.", quote: "The royalty module alone paid for the course ten times over." },
  { name: "Grace Youth Choir", quote: "We came in as musicians. We left with a ministry strategy." },
];

export default function ArtistDevelopment() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Artist Development Enquiry", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Application received — we'll be in touch within 48 hours.");
      setForm({ name: "", email: "", phone: "", subject: "Artist Development Enquiry", message: "" });
    } catch {
      toast.error("Could not send application");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <section className="relative bg-[#0B1526] text-white py-24 sm:py-32 overflow-hidden" data-testid="adp-hero">
        <img src="https://images.pexels.com/photos/5912280/pexels-photo-5912280.jpeg?auto=compress&cs=tinysrgb&w=1920" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-5">Artist Development Programme</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
              Anointing meets <span className="italic text-[#C00000]">industry.</span>
            </h1>
            <p className="text-white/70 text-lg max-w-lg leading-relaxed">
              A 12-week intensive teaching gospel artists what the music industry won't — royalties,
              contracts, branding, financial literacy, and ministry sustainability.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#apply" className="btn-primary-red" data-testid="action-apply-adp-button">
                Apply Now <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/shop" className="btn-outline-white">Get the Workbook</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 backdrop-blur border border-white/10 p-6"><div className="text-5xl font-display text-[#C00000]">12</div><div className="text-xs tracking-[0.2em] uppercase text-white/60 mt-2">Week Programme</div></div>
            <div className="bg-white/5 backdrop-blur border border-white/10 p-6"><div className="text-5xl font-display text-[#C00000]">6</div><div className="text-xs tracking-[0.2em] uppercase text-white/60 mt-2">Core Modules</div></div>
            <div className="bg-white/5 backdrop-blur border border-white/10 p-6"><div className="text-5xl font-display text-[#C00000]">18+</div><div className="text-xs tracking-[0.2em] uppercase text-white/60 mt-2">Graduates</div></div>
            <div className="bg-white/5 backdrop-blur border border-white/10 p-6"><div className="text-5xl font-display text-[#C00000]">100%</div><div className="text-xs tracking-[0.2em] uppercase text-white/60 mt-2">Certified</div></div>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="adp-modules">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">The Curriculum</div>
          <h2 className="font-display text-4xl lg:text-5xl text-[#1F3864] mb-12 max-w-2xl leading-tight">
            Six modules. Twelve weeks. A lifetime of equipping.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MODULES.map((m, i) => (
              <div key={i} className="border border-gray-200 p-8 hover:border-[#1F3864] transition-colors" data-testid={`adp-module-${i}`}>
                <div className="w-12 h-12 bg-[#1F3864] text-white flex items-center justify-center mb-5">
                  <m.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl text-[#1F3864] mb-3">{m.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] py-20" data-testid="adp-who">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Who Is It For?</div>
          <h2 className="font-display text-4xl text-[#1F3864] mb-10 leading-tight">Emerging gospel artists who want more than a stage.</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Worship leaders writing original music",
              "Independent gospel singers ready to go pro",
              "Choir directors wanting to release recordings",
              "Producers working in the gospel industry",
              "Youth ministries launching artist collectives",
              "Parents of young musical talent",
            ].map((i, k) => (
              <div key={k} className="flex items-start gap-3 bg-white p-5 border-l-4 border-[#C00000]">
                <CheckCircle2 className="w-5 h-5 text-[#1F3864] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">{i}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24" data-testid="adp-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-4xl text-[#1F3864] mb-10 text-center">Graduates speak.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="p-8 bg-[#1F3864] text-white">
                <p className="italic leading-relaxed mb-4">"{t.quote}"</p>
                <div className="text-sm text-[#C00000]">— {t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="py-20 bg-[#F5F5F5]" data-testid="adp-apply">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Apply</div>
          <h2 className="font-display text-4xl text-[#1F3864] mb-8 leading-tight">Ready to begin?</h2>
          <form onSubmit={submit} className="bg-white p-8 space-y-4 border border-gray-200" data-testid="form-adp-apply">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name" className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-adp-name" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-adp-email" />
            </div>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-adp-phone" />
            <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your music and why you want to join..." className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-adp-message" />
            <button type="submit" disabled={busy} className="btn-primary-red w-full justify-center" data-testid="action-submit-adp-application">
              {busy ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
